import React, { useState, useEffect } from "react";
import styles from "./YoutubeUpload.module.css";
import { UploadVideo, videosLogic } from "../../../../Logic/Videos";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress'; // Opcional: si usas Material UI

const YoutubeUpload: React.FC = () => {
  // 1. Estados del Formulario
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [fecha, setFecha] = useState('');
  const [tags, setTags] = useState('');

  // 2. Estados de Control y UI
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null, message: string }>({
    type: null,
    message: ''
  });

  // Función para extraer el ID de video de una URL de YouTube
  const getYoutubeID = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // EFECTO: Autocompletar título cuando cambia la URL
  useEffect(() => {
    const fetchVideoInfo = async () => {
      const videoId = getYoutubeID(url);
      if (videoId) {
        try {
          setFetchingData(true);
          // Usamos oembed de YouTube (Gratis y sin API Key)
          const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
          if (response.ok) {
            const data = await response.json();
            setTitle(data.title || '');
            setStatus({ type: null, message: '' });
          }
        } catch (error) {
          console.error("Error al obtener info del video", error);
        } finally {
          setFetchingData(false);
        }
      }
    };

    // Debounce: espera a que el usuario deje de escribir por 800ms
    const timer = setTimeout(() => {
      if (url.trim() !== "") fetchVideoInfo();
    }, 800);

    return () => clearTimeout(timer);
  }, [url]);

  // Lógica de Subida
  const uploadVideoAction = async () => {
    // Limpiar alertas previas
    setStatus({ type: null, message: '' });

    // Validaciones obligatorias
    if (!title.trim() || !url.trim() || !fecha.trim()) {
      setStatus({ type: 'error', message: "Título, URL y Fecha son obligatorios." });
      return;
    }

    const videoId = getYoutubeID(url);
    if (!videoId) {
      setStatus({ type: 'error', message: "La URL de YouTube no parece válida." });
      return;
    }

    const videoData: UploadVideo = { 
      title: title,
      url: `https://www.youtube.com/watch?v=${videoId}`,
      published_at: fecha,
      tags: tags,
      thumbnail: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` 
    };

    try {
      setLoading(true);
      await videosLogic.UploadVideo(videoData);
      
      // Éxito
      setStatus({ type: 'success', message: "¡Video guardado exitosamente!" });
      
      // Limpiar formulario
      setTitle('');
      setUrl('');
      setFecha('');
      setTags('');

      // Desaparecer mensaje de éxito tras 5 segundos
      setTimeout(() => setStatus({ type: null, message: '' }), 5000);

    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', message: "Error al conectar con el servidor." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.formTitle}>Subir video de Youtube</h1>

      {/* Alertas de Feedback */}
      {status.type && (
        <div className={`${styles.alert} ${status.type === 'success' ? styles.success : styles.error}`}>
          {status.type === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
          <span>{status.message}</span>
        </div>
      )}

      <div className={styles.formGroup}>
        <label>YouTube URL *</label> 
        <div className={styles.inputWrapper}>
          <input 
            type="text" 
            placeholder="Link completo aquí..." 
            value={url} 
            onChange={(e) => setUrl(e.target.value)} 
          />
          {fetchingData && <span className={styles.loadingText}>Buscando título...</span>}
        </div><br></br>

        <label>Título del Video *</label> <br></br>
        <input 
          type="text" 
          placeholder="Se llenará automáticamente o escríbelo" 
          value={title} 
          onChange={(e) => setTitle(e.target.value)} 
        /><br></br><br></br>

        <label>Fecha de Publicación *</label><br></br>
        <input 
          type="date" 
          value={fecha} 
          onChange={(e) => setFecha(e.target.value)} 
        /><br></br><br></br>

        <label>Tags (Opcional)</label> <br></br>
        <input 
          type="text" 
          placeholder="Ej: Paz, Biblia, Pesaj..." 
          value={tags} 
          onChange={(e) => setTags(e.target.value)} 
        /><br></br>
        
        <button 
          className={styles.button} 
          onClick={uploadVideoAction}
          disabled={loading || fetchingData}
        >
          {loading ? "Enviando..." : "Subir Video"}
        </button>
      </div>
    </div>
  );
};

export default YoutubeUpload;